import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBackRounded";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { Breadcrumbs } from "@/components/nav/breadcrumbs";
import { LoadingState, ErrorState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { ConnectorIcon } from "@/components/connectors/connector-icon";
import { SchemaForm, withDefaults } from "@/components/connectors/schema-form";
import { useConnectorMutations, useConnectorTypes } from "@/api/connectors";

export const Route = createFileRoute("/_app/connectors/new")({
  component: NewConnectorPage,
});

function NewConnectorPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const types = useConnectorTypes();
  const { create } = useConnectorMutations();

  const [name, setName] = useState("");
  const [typeValue, setTypeValue] = useState<string | null>(null);
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [templateSearch, setTemplateSearch] = useState("");

  const selectedType = useMemo(
    () => types.data?.find((t) => t.type === typeValue) ?? null,
    [types.data, typeValue],
  );

  const templates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase();
    return (types.data ?? [])
      .filter((t) => !q || `${t.displayName ?? ""} ${t.type}`.toLowerCase().includes(q))
      .sort((a, b) => (a.displayName ?? a.type).localeCompare(b.displayName ?? b.type));
  }, [types.data, templateSearch]);

  if (types.isLoading) return <LoadingState />;
  if (types.error) return <ErrorState error={types.error} onRetry={() => types.refetch()} />;

  async function onSubmit() {
    if (!name || !typeValue) return;
    const created = await create.mutateAsync({
      name,
      type: typeValue,
      config: withDefaults(selectedType?.formSchema, config),
    });
    toast.success("Connector created");
    navigate({ to: "/connectors/$connectorId", params: { connectorId: created.id } });
  }

  return (
    <PageContainer maxWidth={720}>
      <PageHeader
        title="New connector"
        breadcrumbs={
          <Breadcrumbs items={[{ label: "Connectors", to: "/connectors" }, { label: "New" }]} />
        }
      />

      {!selectedType ? (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Pick a connector template to start from.
          </Typography>
          <TextField
            size="small"
            placeholder="Search templates…"
            value={templateSearch}
            onChange={(e) => setTemplateSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ maxWidth: 320 }}
          />
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
            }}
          >
            {templates.map((t) => (
              <Card key={t.type} variant="outlined">
                <CardActionArea
                  sx={{ p: 2, height: "100%" }}
                  onClick={() => {
                    setTypeValue(t.type);
                    setConfig({});
                  }}
                >
                  <Stack spacing={1}>
                    <ConnectorIcon type={t.type} />
                    <Typography variant="subtitle2">{t.displayName ?? t.type}</Typography>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
                      <Chip size="small" variant="outlined" label={t.type} />
                      {t.introspectable ? (
                        <Chip size="small" color="info" variant="outlined" label="Browsable" />
                      ) : null}
                    </Stack>
                  </Stack>
                </CardActionArea>
              </Card>
            ))}
            {templates.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No templates match “{templateSearch}”.
              </Typography>
            ) : null}
          </Box>
        </Stack>
      ) : (
        <Card sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              sx={{ alignSelf: "flex-start" }}
              onClick={() => {
                setTypeValue(null);
                setConfig({});
              }}
            >
              Change template
            </Button>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
              <ConnectorIcon type={selectedType.type} />
              <Typography variant="h4">{selectedType.displayName ?? selectedType.type}</Typography>
            </Stack>
            <TextField
              size="small"
              label="Name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <SchemaForm
              schema={selectedType.formSchema}
              value={config}
              onChange={setConfig}
              secretFields={selectedType.secretFields ?? []}
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" disabled={!name || create.isPending} onClick={onSubmit}>
                Create
              </Button>
              <Button onClick={() => navigate({ to: "/connectors" })}>Cancel</Button>
            </Stack>
          </Stack>
        </Card>
      )}
    </PageContainer>
  );
}
